/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { TFunction } from 'i18next';
import { LessonPreviewWeekData } from '../../types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(4),
      paddingTop: 0
    },
    table: {
      maxWidth: 'unset !important'
    },
    tableBody: {
      background: 'white'
    },
    tableRow: {
      cursor: 'pointer',
      '&:hover': {
        boxShadow: '0 1px 8px 4px rgba(0, 0, 0, 0.20)',
        backgroundColor: theme.palette.grey[100]
      }
    },
    tableRowWithHighlight: {
      cursor: 'pointer',
      backgroundColor: '#e0efff',
      '&:hover': {
        backgroundColor: theme.palette.grey[100]
      }
    },
    tableHead: {
      height: '23px',
      fontWeight: 900,
      lineHeight: '23px',
      fontSize: 14,
      color: '#000000'
    },
    textUppercase: {
      textTransform: 'uppercase'
    },
    imgBox: {
      height: 40,
      width: 40,
      borderRadius: 8
    },
    weekTextContainer: {
      textAlign: 'center'
    }
  };
});

type WeeksTableProps = {
  data: LessonPreviewWeekData[];
};

const SPOT_CHECK_WORDS = ['Letters', 'Words', 'Sentences', 'Paragraph'];
const ENRICHMENT_WORDS = ['My Choice Keyboarding', 'Hear It, Type It'];

const maybeEnrichName = (name: string) =>
  SPOT_CHECK_WORDS.includes(name) ? (
    <b>Spot Check: {name}</b>
  ) : ENRICHMENT_WORDS.includes(name) ? (
    `${name}*`
  ) : (
    name
  );

const renderDay = (item: any, idx: number | undefined, property: string) => (
  <div key={`${property}_${idx}`}>
    <span>
      {item?.[property].map((x: any) => (
        <Typography key={`${x.day}_${x.week}_${x.activityId}`}>
          {maybeEnrichName(x.name)}
        </Typography>
      ))}
    </span>
  </div>
);

const renderWeekAndTitle = (classes: { [key: string]: string }, item: any, idx: number) => {
  return (
    <div key={`${item.week}_${idx}`}>
      <Grid container direction="column">
        <Grid item container>
          <Grid item xs={3}>
            <img src={item.theme.icon} className={classes.imgBox} alt="Week Thumbnail" />
          </Grid>
          <Grid item xs={3}>
            <div className={classes.weekTextContainer}>
              <Typography variant="h4">{item.week}</Typography>
            </div>
          </Grid>
        </Grid>
        <Grid item>
          <Typography>{item?.theme?.name}</Typography>
        </Grid>
      </Grid>
    </div>
  );
};

const WeeksTable: React.FC<WeeksTableProps> = ({ data }) => {
  const classes = useStyles();
  const { t } = useTranslation('WeeksTable');
  const history = useHistory();
  const getColumns: (t: TFunction) => ColumnProps<LessonPreviewWeekData>[] = useCallback(
    (t: TFunction) => [
      {
        id: 'theme',
        label: t('Theme/Week'),
        getValue: (item, idx) => renderWeekAndTitle(classes, item, idx)
      },
      {
        id: 'keys',
        label: t('Keys'),
        getValue: item => (
          <div key={`${item.week}_col_1`} className={classes.textUppercase}>
            <span>
              {item.keys.map((value: string) => (
                <Typography key={`${value}_key`}>{value}</Typography>
              ))}
            </span>
          </div>
        )
      },
      {
        id: 'monday',
        label: t('Monday'),
        getValue: (item, idx) => renderDay(item, idx, 'Monday')
      },
      {
        id: 'tuesday',
        label: t('Tuesday'),
        getValue: (item, idx) => renderDay(item, idx, 'Tuesday')
      },
      {
        id: 'wednesday',
        label: t('Wednesday'),
        getValue: (item, idx) => renderDay(item, idx, 'Wednesday')
      },
      {
        id: 'thursday',
        label: t('Thursday'),
        getValue: (item, idx) => renderDay(item, idx, 'Thursday')
      },
      {
        id: 'friday',
        label: t('Friday'),
        getValue: (item, idx) => renderDay(item, idx, 'Friday')
      }
    ],
    [classes]
  );

  const handleRowClick = (item: LessonPreviewWeekData): void => {
    history.push(`${history.location.pathname}/${item.week}`);
  };

  const columns: ColumnProps<LessonPreviewWeekData>[] = getColumns(t);

  return (
    <div className={classes.container}>
      <LwtTable
        columns={columns}
        data={data}
        onRowClick={handleRowClick}
        options={{ checkbox: false, sortable: false }}
        classes={{
          table: () => classes.table,
          body: () => classes.tableBody,
          row: (item, idx) =>
            [0, 1, 2, 3, 4, 5].includes(idx) ? classes.tableRowWithHighlight : classes.tableRow,
          headRow: () => classes.tableHead
        }}
      />
    </div>
  );
};

export default WeeksTable;
